import Modal from '@src/components/common/Modal';
import { type CSSProperties, useState } from 'react';
import styled from 'styled-components';

import { formatDate } from './formatDate';
import {
  FormActions,
  GhostButton,
  PrimaryButton,
  Select,
  TextInput,
} from './formControls';

import type {
  LanguageRef,
  TranslationMemoryEntry,
  TranslationMemoryEntryInput,
} from '@src/repositories/TranslationMemoryEntryRepository';

/** A real collection option for the move picker (Default is added separately). */
export interface CollectionOption {
  id: string;
  name: string;
}

interface Props {
  mode: 'create' | 'edit';
  /** Languages offered by the workspace (showcase supported languages). */
  languages: LanguageRef[];
  collections: CollectionOption[];
  /**
   * The showcase's default language id — a new entry's source language defaults
   * to it (falling back to the first supported language).
   */
  defaultSourceLanguageId: string | null;
  /** Preselected collection for a new entry (`null` = Default). */
  initialCollectionId: string | null;
  /**
   * Create-mode seeds for the promote-from-review flow (PLAT-10852): the
   * fragment's source text, the reviewer's manual translation, and the run's
   * target language. Deliberately separate props rather than a synthetic
   * `entry` — `entry` also arms the "Do not translate" toggle when the two
   * texts match, which a promoted pair must not do. Settings passes none.
   */
  initialSourceTerm?: string;
  initialTranslation?: string;
  initialTranslationLanguageId?: string;
  /** The entry being edited (create mode leaves this undefined). */
  entry?: TranslationMemoryEntry;
  onClose: () => void;
  /** Resolve `null` on success (modal closes) or an error message to show. */
  onSubmit: (input: TranslationMemoryEntryInput) => Promise<string | null>;
}

const CONTENT_STYLE: CSSProperties = {
  width: 'min(520px, 92vw)',
  background: 'var(--surface)',
  borderRadius: 'var(--radius-modal, 12px)',
  padding: '24px',
};

/**
 * Create / edit a translation-memory entry in a modal (PLAT-10845). Covers every
 * editable field plus the first-class **"Do not translate"** affordance (E4) —
 * toggling it mirrors `translation` to `source_term` and clears the target
 * language (⇒ all languages) so the author never types the term twice — and the
 * **collection** picker, which reassigns the single optional link = "move" (E12).
 */
const EntryFormModal = ({
  mode,
  languages,
  collections,
  defaultSourceLanguageId,
  initialCollectionId,
  initialSourceTerm,
  initialTranslation,
  initialTranslationLanguageId,
  entry,
  onClose,
  onSubmit,
}: Props) => {
  const [sourceTerm, setSourceTerm] = useState(
    entry?.sourceTerm ?? initialSourceTerm ?? '',
  );
  // Create mode defaults to the showcase's default language (not just the first
  // supported one); edit mode keeps the entry's own source language.
  const [sourceLanguageId, setSourceLanguageId] = useState(
    entry?.sourceLanguage?.id ??
      defaultSourceLanguageId ??
      languages[0]?.id ??
      '',
  );
  const [translation, setTranslation] = useState(
    entry?.translation ?? initialTranslation ?? '',
  );
  const [translationLanguageId, setTranslationLanguageId] = useState(
    entry?.translationLanguage?.id ?? initialTranslationLanguageId ?? '',
  );
  const [collectionId, setCollectionId] = useState(
    entry?.collectionId ?? initialCollectionId ?? '',
  );
  const [comment, setComment] = useState(entry?.comment ?? '');
  // Do-not-translate = translation equals the source term (E4). Seed from the
  // entry so an existing DNT rule opens with the toggle on.
  const [dnt, setDnt] = useState(
    !!entry && entry.translation === entry.sourceTerm,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSourceTermChange = (value: string) => {
    setSourceTerm(value);
    if (dnt) setTranslation(value);
  };

  const toggleDnt = () => {
    setDnt(prev => {
      const next = !prev;
      if (next) {
        setTranslation(sourceTerm);
        setTranslationLanguageId('');
      }
      return next;
    });
  };

  const effectiveTranslation = dnt ? sourceTerm : translation;
  const canSubmit =
    !!sourceTerm.trim() &&
    !!sourceLanguageId &&
    !!effectiveTranslation.trim() &&
    !busy;

  const submit = async () => {
    if (!canSubmit) return;
    const sourceLanguage = languages.find(l => l.id === sourceLanguageId);
    if (!sourceLanguage) {
      setError('Please choose a source language.');
      return;
    }
    const translationLanguage = dnt
      ? null
      : (languages.find(l => l.id === translationLanguageId) ?? null);

    setBusy(true);
    setError(null);
    const message = await onSubmit({
      sourceTerm: sourceTerm.trim(),
      sourceLanguage,
      translation: effectiveTranslation.trim(),
      translationLanguage,
      comment: comment.trim(),
      collectionId: collectionId || null,
    });
    setBusy(false);
    if (message) setError(message);
    else onClose();
  };

  return (
    <Modal isOpen closeModal={onClose} contentStyle={CONTENT_STYLE}>
      <Form
        onSubmit={e => {
          e.preventDefault();
          void submit();
        }}
      >
        <h2>{mode === 'create' ? 'Add entry' : 'Edit entry'}</h2>

        <Field>
          <label htmlFor="tm-source-term">Source term</label>
          <TextInput
            id="tm-source-term"
            autoFocus
            value={sourceTerm}
            placeholder="e.g. Merck"
            onChange={e => onSourceTermChange(e.target.value)}
          />
        </Field>

        <Field>
          <label htmlFor="tm-source-lang">Source language</label>
          <Select
            id="tm-source-lang"
            value={sourceLanguageId}
            onChange={e => setSourceLanguageId(e.target.value)}
          >
            <option value="" disabled>
              Select a language
            </option>
            {languages.map(l => (
              <option key={l.id} value={l.id}>
                {l.caption ?? l.value ?? l.id}
              </option>
            ))}
          </Select>
        </Field>

        <DntToggle>
          <input
            type="checkbox"
            checked={dnt}
            onChange={toggleDnt}
            id="tm-dnt"
          />
          <label htmlFor="tm-dnt">
            Do not translate <span>(keep this term verbatim)</span>
          </label>
        </DntToggle>

        {!dnt && (
          <>
            <Field>
              <label htmlFor="tm-translation">Translation</label>
              <TextInput
                id="tm-translation"
                value={translation}
                placeholder="Target term"
                onChange={e => setTranslation(e.target.value)}
              />
            </Field>

            <Field>
              <label htmlFor="tm-target-lang">Target language</label>
              <Select
                id="tm-target-lang"
                value={translationLanguageId}
                onChange={e => setTranslationLanguageId(e.target.value)}
              >
                <option value="">All languages</option>
                {languages.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.caption ?? l.value ?? l.id}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}

        <Field>
          <label htmlFor="tm-collection">Collection</label>
          <Select
            id="tm-collection"
            value={collectionId}
            onChange={e => setCollectionId(e.target.value)}
          >
            <option value="">Default (uncategorized)</option>
            {collections.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <label htmlFor="tm-comment">Comment</label>
          <Textarea
            id="tm-comment"
            value={comment}
            placeholder="Optional note"
            onChange={e => setComment(e.target.value)}
          />
        </Field>

        {mode === 'edit' && entry && (
          <MetaBlock>
            <div>
              <span className="who">
                Created by {entry.createdByUser?.name ?? 'Unknown'}
              </span>
              <span className="when">{formatDate(entry.createdAt)}</span>
            </div>
            <div>
              <span className="who">
                Last updated by {entry.lastUpdatedByUser?.name ?? 'Unknown'}
              </span>
              <span className="when">{formatDate(entry.updatedAt)}</span>
            </div>
          </MetaBlock>
        )}

        {error && <ErrorText role="alert">{error}</ErrorText>}

        <FormActions>
          <GhostButton type="button" onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton type="submit" disabled={!canSubmit}>
            {mode === 'create' ? 'Add entry' : 'Save'}
          </PrimaryButton>
        </FormActions>
      </Form>
    </Modal>
  );
};

export default EntryFormModal;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;

  > h2 {
    font-size: var(--text-lg);
    font-weight: var(--font-weight-bold);
    color: var(--foreground);
    margin: 0 0 2px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  > label {
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 60px;
  resize: vertical;
  padding: 8px 10px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  color: var(--foreground);
  font-size: var(--text-sm);
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const DntToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > input {
    cursor: pointer;
  }

  > label {
    font-size: var(--text-sm);
    color: var(--foreground);
    cursor: pointer;

    > span {
      color: var(--text-muted);
      font-size: var(--text-xs);
    }
  }
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  color: var(--destructive);
`;

// Provenance footer (edit mode): who created / last updated the entry + dates.
const MetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 10px;
  border-top: 1px solid var(--border);

  > div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }

  .who {
    font-size: var(--text-xs);
    color: var(--text-secondary);
  }

  .when {
    font-size: var(--text-xs);
    color: var(--text-muted);
    white-space: nowrap;
  }
`;
